import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchData, logout } from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import axios from "axios";
import { usePermissions } from "../../permissions/usePermissions";

export default function Sidebar({ isMobileOpen = false, onClose }) {
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showHolidaysActions, setShowHolidaysActions] = useState(false);
  const navRef = useRef(null);
  const quickActionsSubmenuRef = useRef(null);
  const holidaysSubmenuRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { can, user } = usePermissions();
  const v = (m) => can(m, "view");

  useEffect(() => {
    if (showQuickActions && navRef.current && quickActionsSubmenuRef.current) {
      quickActionsSubmenuRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [showQuickActions]);

  useEffect(() => {
    if (showHolidaysActions && navRef.current && holidaysSubmenuRef.current) {
      holidaysSubmenuRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [showHolidaysActions]);

  const closeMenus = () => {
    setShowQuickActions(false);
    setShowHolidaysActions(false);
    if (onClose) onClose();
  };

  const handleLogoutClick = () => {
    dispatch(logout());
    navigate("/login");
    closeMenus();
  };

  const handleResetAllLoginCounters = async () => {
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      await axios.post(
        `https://api.alfares.cloud/api/alfares/resetAllLoginCounters/${credentials?.token}`
      );
      toast.success("تم تصفير عداد الدخول لجميع المدارس");
      dispatch(fetchData());
    } catch {
      toast.error("فشل في تصفير العدادات");
    }
  };

  const handleResetSchoolStatus = async () => {
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const res = await fetch(
        `https://api.alfares.cloud/api/alfares/schools/resetSchoolStatus/${credentials?.token}`,
        { method: "POST" }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success("تم تغيير حالة المدارس بنجاح");
        dispatch(fetchData());
      } else toast.error(data.message || "حدث خطأ");
    } catch {
      toast.error("فشل الاتصال");
    }
  };

  const NavGroup = ({ title, children }) => (
    <div className="sidebar__group">
      <h3 className="sidebar__groupTitle">{title}</h3>
      {children}
    </div>
  );

  return (
    <aside
      className={`sidebar ${isMobileOpen ? "sidebar--open" : ""}`}
      aria-label="القائمة الجانبية"
    >
      <div className="sidebar__brand">
        <p className="sidebar__brandTitle">لوحة التحكم</p>
        <p className="sidebar__brandSub">الفارس في سكرتاريا المدارس</p>
      </div>

      <nav className="sidebar__nav" ref={navRef}>
        {v("dashboard") && (
          <NavGroup title="الرئيسية">
            <NavLink
              to="/home"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-house-chimney nav-item__icon"
                style={{ color: "var(--accent)" }}
              ></i>
              <span className="nav-item__label">الشاشة الرئيسية</span>
            </NavLink>
          </NavGroup>
        )}

        {(can("quick_controls", "edit") || v("notifications")) && (
          <NavGroup title="الإجراءات السريعة">
            {can("quick_controls", "edit") && (
              <>
                <div
                  className={`nav-item nav-item--quick-controls ${showQuickActions ? "active" : ""}`}
                  onClick={() => {
                    const nextState = !showQuickActions;
                    setShowQuickActions(nextState);
                    setShowHolidaysActions(false);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <i
                    className="fa-solid fa-bolt nav-item__icon"
                    style={{ color: "#fbbf24" }}
                  ></i>
                  <span className="nav-item__label">مركز التحكم السريع</span>
                  <i
                    className={`fa-solid fa-chevron-down ms-auto transition-transform ${
                      showQuickActions ? "rotate-180" : ""
                    }`}
                    style={{ fontSize: "10px", opacity: 0.5 }}
                  ></i>
                </div>

                {showQuickActions && (
                  <div
                    ref={quickActionsSubmenuRef}
                    className="sidebar__submenu anim-slide-down"
                  >
                    <button
                      onClick={() => {
                        handleResetAllLoginCounters();
                        setShowHolidaysActions(false);
                      }}
                      className="nav-subitem"
                    >
                      <i className="fa-solid fa-rotate-left"></i>
                      <span>تصفير الدخول</span>
                    </button>
                    <button
                      onClick={() => {
                        handleResetSchoolStatus();
                        setShowHolidaysActions(false);
                      }}
                      className="nav-subitem danger"
                    >
                      <i className="fa-solid fa-shield-halved"></i>
                      <span>حالة المدارس</span>
                    </button>
                  </div>
                )}
              </>
            )}

            {v("notifications") && (
              <NavLink
                to="/notifications"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-bell nav-item__icon"
                  style={{ color: "#ef4444" }}
                ></i>
                <span className="nav-item__label">التنبيهات لمصمم الشهادات</span>
              </NavLink>
            )}
          </NavGroup>
        )}

        {v("schools") && (
          <NavGroup title="إدارة المدارس">
            <NavLink
              to="/schools"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-school nav-item__icon"
                style={{ color: "#f59e0b" }}
              ></i>
              <span className="nav-item__label">جميع المدارس</span>
            </NavLink>
            {can("schools", "add") && (
              <NavLink
                to="/add-school"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-plus-square nav-item__icon"
                  style={{ color: "#10b981" }}
                ></i>
                <span className="nav-item__label">إضافة مدرسة جديدة</span>
              </NavLink>
            )}
            <NavLink
              to="/school-renewal"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-sync nav-item__icon"
                style={{ color: "#8b5cf6" }}
              ></i>
              <span className="nav-item__label">الراغبة في التجديد</span>
            </NavLink>
            <NavLink
              to="/extend-subscription"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-calendar-plus nav-item__icon"
                style={{ color: "#10b981" }}
              ></i>
              <span className="nav-item__label">تمديد الاشتراك</span>
            </NavLink>
            <NavLink
              to="/quota"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-table-cells nav-item__icon"
                style={{ color: "#0ea5e9" }}
              ></i>
              <span className="nav-item__label">نصاب الحصص</span>
            </NavLink>
          </NavGroup>
        )}

        {(v("employees") || v("archive") || v("school_notes")) && (
          <NavGroup title="شؤون الموظفين">
            {v("employees") && (
              <>
                <NavLink
                  to="/find-teachers"
                  onClick={closeMenus}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <i
                    className="fa-solid fa-users-viewfinder nav-item__icon"
                    style={{ color: "#6366f1" }}
                  ></i>
                  <span className="nav-item__label">البحث عن مدرسة او موظف</span>
                </NavLink>
                <NavLink
                  to="/transferred-teachers"
                  onClick={closeMenus}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <i
                    className="fa-solid fa-people-arrows nav-item__icon"
                    style={{ color: "#ec4899" }}
                  ></i>
                  <span className="nav-item__label">حركات المعلمين</span>
                </NavLink>
                <NavLink
                  to="/half-mrkaz"
                  onClick={closeMenus}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <i
                    className="fa-solid fa-user-clock nav-item__icon"
                    style={{ color: "#8b5cf6" }}
                  ></i>
                  <span className="nav-item__label">النصف مركز</span>
                </NavLink>
              </>
            )}
            {v("archive") && (
              <NavLink
                to="/archive"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-box-archive nav-item__icon"
                  style={{ color: "#f59e0b" }}
                ></i>
                <span className="nav-item__label">الأرشيف</span>
              </NavLink>
            )}
            {v("school_notes") && (
              <NavLink
                to="/school-notes"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-envelope-open-text nav-item__icon"
                  style={{ color: "#10b981" }}
                ></i>
                <span className="nav-item__label">رسائل المستخدمين</span>
              </NavLink>
            )}
          </NavGroup>
        )}

        {v("finance") && (
          <NavGroup title="النظام المالي">
            <NavLink
              to="/finance"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-wallet nav-item__icon"
                style={{ color: "#fbbf24" }}
              ></i>
              <span className="nav-item__label">الشؤون المالية</span>
            </NavLink>
          </NavGroup>
        )}

        {(v("tarwees") || v("holidays") || v("versions")) && (
          <NavGroup title="الإعدادات العامة">
            {v("tarwees") && (
              <NavLink
                to="/tarwees"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-heading nav-item__icon"
                  style={{ color: "#3b82f6" }}
                ></i>
                <span className="nav-item__label">ترويسة الوزارة</span>
              </NavLink>
            )}

            {v("holidays") && (
              <>
                <div
                  className={`nav-item ${showHolidaysActions ? "active" : ""}`}
                  onClick={() => {
                    const nextState = !showHolidaysActions;
                    setShowHolidaysActions(nextState);
                    setShowQuickActions(false);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <i
                    className="fa-solid fa-calendar-days nav-item__icon"
                    style={{ color: "#f97316" }}
                  ></i>
                  <span className="nav-item__label">العطل والمناسبات</span>
                  <i
                    className={`fa-solid fa-chevron-down ms-auto transition-transform ${
                      showHolidaysActions ? "rotate-180" : ""
                    }`}
                    style={{ fontSize: "10px", opacity: 0.5 }}
                  ></i>
                </div>

                {showHolidaysActions && (
                  <div
                    ref={holidaysSubmenuRef}
                    className="sidebar__submenu anim-slide-down"
                  >
                    <NavLink
                      to="/holidays"
                      onClick={closeMenus}
                      className="nav-subitem"
                    >
                      <i className="fa-solid fa-calendar-minus"></i>
                      <span>العطل السنوية</span>
                    </NavLink>
                    <NavLink
                      to="/annual-events"
                      onClick={closeMenus}
                      className="nav-subitem"
                    >
                      <i className="fa-solid fa-calendar-check"></i>
                      <span>المناسبات</span>
                    </NavLink>
                    <NavLink
                      to="/school-events"
                      onClick={closeMenus}
                      className="nav-subitem"
                    >
                      <i className="fa-solid fa-calendar-day"></i>
                      <span>أيام الدوام</span>
                    </NavLink>
                    <NavLink
                      to="/tasks-holidays"
                      onClick={closeMenus}
                      className="nav-subitem"
                    >
                      <i className="fa-solid fa-list-check"></i>
                      <span>أيام المهمات</span>
                    </NavLink>
                  </div>
                )}
              </>
            )}

            {v("versions") && (
              <NavLink
                to="/publish-new-version"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-cloud-arrow-up nav-item__icon"
                  style={{ color: "#8b5cf6" }}
                ></i>
                <span className="nav-item__label">إصدار جديد للفارس</span>
              </NavLink>
            )}
          </NavGroup>
        )}

        {
          <NavGroup title="إدارة النظام">
            <NavLink
              to="/account"
              onClick={closeMenus}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <i
                className="fa-solid fa-circle-user nav-item__icon"
                style={{ color: "#6366f1" }}
              ></i>
              <span className="nav-item__label">حسابي</span>
            </NavLink>
            {v("users") && (
              <NavLink
                to="/users"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-users-gear nav-item__icon"
                  style={{ color: "#14b8a6" }}
                ></i>
                <span className="nav-item__label">المستخدمون</span>
              </NavLink>
            )}
            {v("roles") && (
              <NavLink
                to="/roles"
                onClick={closeMenus}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <i
                  className="fa-solid fa-user-shield nav-item__icon"
                  style={{ color: "#c9952c" }}
                ></i>
                <span className="nav-item__label">الأدوار والصلاحيات</span>
              </NavLink>
            )}
          </NavGroup>
        }

      </nav>

      <div
        className="sidebar__footer"
        style={{
          display: "flex",
          flexDirection: "column",
          marginTop: "auto",
          borderTop: "1px solid var(--border)",
          padding: "10px 10px 14px",
          gap: 8,
        }}
      >
        {user && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 10px",
              background: "rgba(255,255,255,0.04)",
              borderRadius: 6,
            }}
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 6,
                background: "var(--accent)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 15,
                flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-user"></i>
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  color: "#fff",
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.fullName || user.username}
              </span>
              <span
                style={{
                  display: "block",
                  color: "var(--gold)",
                  fontSize: 11,
                  opacity: 0.9,
                }}
              >
                {user.roleName || "مستخدم"}
              </span>
            </span>
          </div>
        )}
        <button
          type="button"
          className="nav-item"
          onClick={handleLogoutClick}
          style={{ borderRadius: "8px" }}
        >
          <i
            className="fa-solid fa-right-from-bracket nav-item__icon"
            style={{ color: "#ef4444" }}
          ></i>
          <span className="nav-item__label">خروج</span>
        </button>
      </div>
    </aside>
  );
}
